/** Temas de ayuda · Combos y recetas (spec 20): la lista, la ventana del combo y la ventana para venderlo. */
import type { TemaAyuda } from '../tipos.ts';

/** Crear y cambiar combos: «Descuentos programados», que por plantilla tiene la dueña. */
const CONFIGURA = { clave: 'productos.descuentos' } as const;
const NO_CONFIGURA = { clave: 'productos.descuentos', sin: true } as const;

export const TEMAS_COMBOS: TemaAyuda[] = [
  {
    id: 'combos',
    titulo: 'Combos y recetas',
    rutas: ['/productos/combos'],
    permiso: { clave: 'productos.consultar' },
    resumen: 'Un combo vende de una vez un trabajo de varios productos: un kit con cantidades fijas o una receta por medida, como el techo en dry wall por m². El descuento del combo solo vale si se vende completo.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Busca por código o nombre. En **Estado** elige **Activos**, **Desactivados** o **Todos**.',
          '**Tipo**: **Combo** (cantidades fijas: se venden 1, 2 o 3 combos) o **Receta por m²** (las cantidades salen de la medida del trabajo). Una receta también puede ir por metro lineal, por m³ o por unidades.',
          '**Productos**: cuántos productos lleva. **Descuento**: el del combo para todos sus productos; un producto puede traer el suyo.',
          '**Estado**: **Activo** o **Desactivado**. **Producto desactivado** quiere decir que uno de sus productos está desactivado: el combo no se puede vender hasta corregirlo.',
          'Un clic en la fila abre el combo. Salen 25 por página: **Anterior** y **Siguiente** cambian de página.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Crear un combo o una receta',
        permiso: CONFIGURA,
        pasos: [
          'En **Productos**, pulsa **Combos y recetas**.',
          'Pulsa **Nuevo combo o receta**.',
          'Llena los datos y agrega los productos. La ayuda de la ventana explica cada campo.',
          'En **Probar** escribe una medida y revisa las cantidades contra tu cuenta.',
          'Pulsa **Guardar combo**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cómo se vende',
        items: [
          'En el punto de venta y en las cotizaciones, el botón **Combo o receta** pone todos sus productos en el carrito con sus cantidades.',
          'El descuento del combo vale mientras el combo esté completo. Si se quita o se cambia uno de sus productos, el combo se desarma y pierde el descuento.',
          'Si un producto tiene además un descuento programado, rige el mayor de los dos.',
        ],
      },
      { tipo: 'nota', texto: 'Los combos los crea y los cambia la dueña. Tú los ves aquí y los vendes desde el punto de venta o la cotización.', permiso: NO_CONFIGURA },
      { tipo: 'nota', texto: 'Un combo no se borra: se desactiva. Lo ya vendido no cambia aunque después se edite o se desactive el combo. Una cotización guarda su combo, pero al cobrarla en el punto de venta se usa la receta de ese momento.' },
    ],
    mensajes: [{ mensaje: 'No se pudo cargar', que_hacer: 'Se cayó la conexión con el servidor; los combos no han desaparecido. Pulsa **Reintentar** cuando vuelva.' }],
    manual: '14.7',
    relacionados: ['productos', 'pos', 'cotizaciones', 'productos.descuentos'],
    claves: ['combo', 'receta', 'kit', 'dry wall', 'drywall', 'por m2', 'metro cuadrado', 'trabajo', 'proyecto', 'paquete'],
  },
  {
    id: 'combos.editar',
    padre: 'combos',
    titulo: 'Crear o cambiar un combo',
    resumen: 'La ventana del combo: sus datos, sus productos con la forma en que sale la cantidad de cada uno, el descuento y la prueba con una medida.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Llenar el combo',
        permiso: CONFIGURA,
        pasos: [
          'Escribe el **Código** (no se repite) y el **Nombre**. La **Descripción** es opcional.',
          'En **Tipo** elige **Combo de cantidades fijas** (se venden 1, 2 o 3 combos) o **Receta por medida** (las cantidades salen de la medida del trabajo).',
          'En una receta, elige la **Unidad de medida**: m², m lineales, m³ o unidades.',
          'Si todo el combo lleva descuento, escríbelo en **Descuento para todos %**.',
          'Pulsa **Agregar producto**, búscalo y elígelo. Entra con la presentación de una unidad; si se vende en caja o en bulto, cámbiala en **Presentación**.',
          'En cada producto elige **Cómo sale la cantidad** y escribe su número: la cantidad, el consumo o lo que cubre.',
          'Si se vende en caja, cuñete o rollo, escribe en **Contenido de la unidad que se vende** lo que trae: caja de 100 tornillos = 100; cuñete de 28 kg = 28.',
          'Con las flechas cambias el orden, con el botón de cambiar pones otro producto en esa fila y con la papelera lo quitas.',
          'Escribe una medida en **Probar**, revisa las cantidades y pulsa **Guardar combo** (o **Guardar cambios**).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cómo sale la cantidad (techo en dry wall de 26 m²)',
        items: [
          '**Cantidad fija**: siempre la misma. En un combo se multiplica por el número de combos: 2 boquillas por combo y 3 combos son 6 boquillas. En una receta va una sola vez por trabajo.',
          '**Consumo por m²**: la medida por lo que se gasta en cada m². Lámina 3/8: 26 × 0,36 = 9,36, se venden 10 láminas.',
          'Con contenido: tornillos, 20 por m² en cajas de 100. 26 × 20 ÷ 100 = 5,2, se venden 6 cajas.',
          '**Rendimiento**: la medida entre lo que cubre cada uno. Suspensión de 10 m² con láminas que cubren 0,72 m² cada una: 10 ÷ 0,72 = 13,89, se venden 14 láminas.',
          '**Derivado**: sale de otro producto del combo con un **Ajuste %**. La secundaria son las láminas menos 10 %: 13,89 menos 10 % = 12,5, se venden 13.',
          'El derivado se calcula con la cantidad del otro producto antes de redondear; se redondea al final.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Redondeo, descuentos y empaques',
        items: [
          '**Redondeo**: **Hacia arriba al entero** (1,3 se vende como 2; 3 queda en 3) o **Hacia arriba a la fracción mínima del producto** (el cable por metros: 12,34 m). La fracción solo va con la presentación de una unidad.',
          '**Desc. propio %**: el descuento de ese producto dentro del combo. Vacío, lleva el del combo.',
          '**Empaques alternativos**: otro empaque del mismo material para completar sin pasarse. Cinta en rollos de 150 m y de 76 m: para 23,4 m va 1 rollo de 76.',
          'Si con los empaques chicos se llega a uno grande, va el grande: para 81 m va 1 rollo de 150, no 2 de 76.',
          'Lo que trae el empaque va en la misma unidad que el consumo (metros, kg).',
          'Un producto no se repite en el combo, tampoco como empaque de otro. Un servicio no puede ir en un combo.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Probar con una medida',
        pasos: [
          'En **Probar** escribe la **Medida del trabajo** o, en un combo, **¿Cuántos combos?**.',
          'La tabla dice cada producto, cómo sale la cantidad, la cantidad y el descuento del combo.',
          'Si hay cambios sin guardar, la prueba usa lo que está en pantalla; si no, el combo guardado, igual que el punto de venta.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Duplicar, desactivar y activar',
        permiso: CONFIGURA,
        items: [
          '**Duplicar** copia el combo guardado con otro código y otro nombre. Sirve para tener una receta por color: duplica y cambia los productos del color.',
          '**Desactivar** pide confirmar: no se puede agregar al punto de venta ni a cotizaciones hasta activarlo. Lo ya vendido no cambia. **Activar** lo vuelve a poner a la venta.',
          '**Guardar cambios** reemplaza la receta. Lo ya vendido conserva la de antes. Un combo que esté en un carrito sin cobrar, o en una cotización que se pasa al punto de venta, hay que quitarlo y volver a agregarlo.',
          'Cada cambio queda en la auditoría con el antes y el después.',
        ],
      },
      { tipo: 'nota', texto: 'Solo la dueña crea y cambia combos, porque valen en todas las tiendas. Tú puedes ver cómo está armado y probarlo con una medida.', permiso: NO_CONFIGURA },
    ],
    mensajes: [
      { mensaje: 'El código es obligatorio', que_hacer: 'Escribe el **Código** del combo.' },
      { mensaje: 'Agrega al menos un producto', que_hacer: 'Pulsa **Agregar producto** y elige el primero.' },
      { mensaje: 'Elige la unidad de la medida (m², m lineales, m³ o unidades)', que_hacer: 'En una receta, elige la **Unidad de medida**.' },
      { mensaje: 'Escribe el factor (la cantidad, el consumo o el rendimiento)', que_hacer: 'Escribe el número de ese producto: la cantidad, lo que se gasta por m² o lo que cubre cada uno.' },
      { mensaje: 'Indica de qué componente sale y el ajuste %', que_hacer: 'En el derivado, elige en **Sale del producto** de cuál sale.' },
      { mensaje: 'El componente base no puede ser otro derivado', que_hacer: 'Elige un producto que no sea derivado.' },
      { mensaje: 'Un producto no puede estar dos veces en el mismo combo (tampoco como empaque de otro)', que_hacer: 'Quita el repetido.' },
      { mensaje: 'Debe ser mayor que cero', que_hacer: 'El número, el contenido y lo que trae cada empaque van mayores que cero.' },
      { mensaje: 'Ya hay un combo con el código …', que_hacer: 'Usa otro código.' },
      { mensaje: '… está desactivado: actívalo en Productos o quítalo del combo', que_hacer: 'Activa el producto en su ficha, o quítalo del combo.' },
      { mensaje: '… es un servicio: un combo solo lleva productos de inventario', que_hacer: 'Quita el servicio del combo.' },
      { mensaje: '…: el redondeo a la fracción mínima solo va con la presentación de una unidad; en esta presentación usa «Hacia arriba al entero»', que_hacer: 'Cambia el **Redondeo** a **Hacia arriba al entero**, o elige la presentación de una unidad.' },
      { mensaje: 'El ajuste no puede pasar de 1.000 %', que_hacer: 'Revisa el **Ajuste %** del derivado.' },
    ],
    manual: '14.7',
    relacionados: ['combos.agregar', 'productos-ficha'],
    claves: ['receta', 'consumo', 'rendimiento', 'derivado', 'contenido', 'redondeo', 'empaque', 'duplicar', 'probar', 'Excel'],
  },
  {
    id: 'combos.agregar',
    padre: 'combos',
    titulo: 'Vender un combo o una receta',
    resumen: 'La ventana del punto de venta y de la cotización para poner un combo en el carrito: eliges el combo, escribes la medida y entran todos sus productos con el descuento del combo.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Agregar un combo al carrito',
        pasos: [
          'En el punto de venta (o en la cotización), pulsa **Combo o receta**.',
          'Busca el combo por nombre o código y elígelo.',
          'Escribe la **Medida del trabajo** (por ejemplo 26 m²) o, si es un combo, **¿Cuántos combos?**.',
          'Revisa la tabla: cada producto, cómo sale la cantidad, la cantidad y el descuento del combo.',
          'Pulsa **Agregar al carrito** (en la cotización, **Agregar a la cotización**).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'En el carrito',
        items: [
          'El combo sale como un bloque con su nombre y su medida, y sus productos debajo.',
          '**Cambiar medida** vuelve a calcular las cantidades con otra medida y conserva el descuento.',
          'La cantidad de un producto del combo no se cambia suelta: cambia la medida o desarma el combo.',
          '**Desarmar** deja sus productos como renglones normales, sin el descuento del combo. Quitar uno de sus productos (la papelera) también lo desarma. Antes pide confirmar.',
          'Cambiar el almacén de donde sale un producto no desarma el combo.',
          '**Quitar combo** saca todos sus productos del carrito.',
          'La factura impresa lleva solo los productos, cada uno con su precio y su descuento.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'El descuento del combo solo vale si se vende completo. Si la dueña cambió o desactivó el combo mientras estaba en el carrito, al cobrar sale un aviso: quítalo y vuelve a agregarlo.' },
    ],
    mensajes: [
      { mensaje: 'El combo «…» está desactivado', que_hacer: 'La dueña lo desactivó. Vende los productos sueltos o pídele que lo active.' },
      { mensaje: 'El combo «…» tiene un producto desactivado (…): corrígelo en Combos y recetas', que_hacer: 'Avísale a la dueña. Mientras tanto, vende los productos sueltos.' },
      { mensaje: 'El combo «…» no coincide con su receta (se cambió la receta o un renglón): quítalo del carrito y vuelve a agregarlo', que_hacer: 'Pulsa **Quitar combo** y agrégalo de nuevo con **Combo o receta**.' },
      { mensaje: 'El número de combos es entero (1, 2, 3…)', que_hacer: 'Escribe un número entero de combos.' },
      { mensaje: 'La medida tiene que ser mayor que cero', que_hacer: 'Escribe la medida del trabajo.' },
      { mensaje: 'El número de combos tiene que ser mayor que cero', que_hacer: 'Escribe cuántos combos (1, 2, 3…).' },
    ],
    manual: '4.12',
    relacionados: ['pos', 'cotizaciones', 'combos.editar'],
    claves: ['combo', 'receta', 'kit', 'medida', 'cambiar medida', 'desarmar', 'quitar combo', 'm2', 'dry wall'],
  },
];
